import React, { useEffect } from 'react'
import { Button } from 'react-bootstrap'
import CustomModal from '../../CustomModal/CustomModal'
import styles from './FreeTrialModal.module.css'
import { getAllPlanPricesForCurrency } from '../../../constants/plans'
import { getAreaCurrency, TIMEZONE } from '../../../constants/currency'
import { isMobileMode } from '../../ProPlans/PricingModalContent'
import { addUpgradeReason } from 'utils/general'

const benefitsPro = [
  {
    icon: '/assets/icons/tv-free.svg',
    text: '15 active projects'
  },
  {
    icon: '/assets/icons/cloud-arrow-up-free.svg',
    text: '10 GB upload limit'
  },
  {
    icon: '/assets/icons/chart-bar-free.svg',
    text: '500,000 visitors /mo'
  },
  {
    icon: '/assets/icons/plus-circle-pro.svg',
    text: 'Everything in Solo Plan'
  },
  {
    icon: '/assets/icons/envelope-pro.svg',
    text: 'Capture emails'
  },
  {
    icon: '/assets/icons/users-pro.svg',
    text: 'Add more team members'
  }
]

const benefitsSolo = [
  {
    icon: '/assets/icons/tv-free.svg',
    text: '5 active projects'
  },
  {
    icon: '/assets/icons/plus-circle-pro.svg',
    text: 'Everything in Tiny Plan'
  },
  {
    icon: '/assets/icons/cloud-arrow-up-free.svg',
    text: '75MB upload limit'
  },
  {
    icon: '/assets/icons/world-yellow.svg',
    text: 'Custom domains'
  },
  {
    icon: '/assets/icons/chart-bar-free.svg',
    text: '100,000 visitors /mo'
  },
  {
    icon: '/assets/icons/padlock-yellow.svg',
    text: 'Password protection'
  }
]

const timeline = [
  {
    icon: '/assets/icons/lock-open.svg',
    title: 'Today',
    description: 'Unlock all features of the plan',
    isActive: true
  },
  {
    icon: '/assets/icons/bell-alert.svg',
    title: 'Day 5',
    description: 'Get a reminder that your trial is about to end'
  },
  {
    icon: '/assets/icons/logo_icon_dark.svg',
    title: 'Day 7',
    description: 'Your subscription starts. Cancel anytime.'
  }
]

const FreeTrialModal = ({
  show,
  onHide,
  onStartTrial,
  onSkip,
  loading = false,
  planType = 'pro',
  hideSkip = false
}) => {
  useEffect(() => {
    if (show && !isMobileMode()) {
      addUpgradeReason(planType === 'solo' ? 'Solo Free Trial' : 'Pro Free Trial')
    }
  }, [show, planType])

  if (isMobileMode()) {
    return null
  }

  const planPrices = getAllPlanPricesForCurrency('monthly', getAreaCurrency(TIMEZONE))
  const isPlanTypePro = planType === 'pro'

  const planPriceText = isPlanTypePro
    ? planPrices?.proPlan?.value
      ? `${planPrices.proPlan.symbol}${planPrices.proPlan.value}/month`
      : '$38/month'
    : planPrices?.soloPlan?.value
      ? `${planPrices.soloPlan.symbol}${planPrices.soloPlan.value}/month`
      : '$18/month'

  const benefits = isPlanTypePro ? benefitsPro : benefitsSolo
  const planName = isPlanTypePro ? 'Pro Plan' : 'Solo Plan'

  // Update timeline description for different plan types
  const updatedTimeline = timeline.map((item) => {
    if (item.title === 'Today') {
      return {
        ...item,
        description: `Unlock all features of the ${planName}`
      }
    }
    return item
  })

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      aria-labelledby="free-trial-modal"
      centered
      enforceFocus={false}
      size="md"
      className={styles.modal}
      mobileCloseButton={!hideSkip}
      backdrop={hideSkip ? 'static' : true}
      keyboard={!hideSkip}
    >
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          {!hideSkip && (
            <button className={styles.closeButton} onClick={onHide}>
              <img src="/assets/icons/cancel_white.svg" height="15" width="15" alt="cancel" />
            </button>
          )}
          <h2 className={styles.title}>Start your free trial</h2>
          <p className={styles.subtitle}>
            Enjoy these benefits on your free trial of the <span className={styles.proPlanBadge}>{planName}</span>
          </p>
        </div>

        {/* Benefits Grid */}
        <div className={styles.benefitsGrid}>
          {benefits.map((benefit, index) => (
            <div key={index} className={styles.benefitItem}>
              <img src={benefit.icon} alt="icon" className={`${styles.benefitIcon} ${styles.iconBgColor}`} />
              <span className={styles.benefitText}>{benefit.text}</span>
              <img src="/assets/icons/check-free.svg" alt="check" className={styles.benefitIcon} />
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className={styles.timelineContainer}>
          <div className={styles.timeline}>
            {updatedTimeline.map((item, index) => (
              <div key={index} className={`${styles.timelineItem} ${item.isActive ? styles.active : ''}`}>
                <div className={styles.timelineIcon}>
                  <img src={item.icon} alt="" height="16" width="16" />
                </div>
                <div className={styles.timelineContent}>
                  <h4 className={styles.timelineTitle}>{item.title}</h4>
                  <p className={styles.timelineDescription}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <p className={styles.pricingText}>
            Free access for 7 days, then <span className={styles.pricingTextBold}>{planPriceText}</span>
          </p>

          <Button
            className={styles.startTrialButton}
            onClick={onStartTrial}
            disabled={loading}
            size="lg"
            data-testid="FreeTrialModal-button-start-trial"
          >
            {loading ? 'Starting...' : 'Start free trial'}
          </Button>

          {!hideSkip && (
            <Button
              variant="link"
              className={styles.skipButton}
              onClick={onSkip}
              data-testid="FreeTrialModal-button-skip"
            >
              Skip
            </Button>
          )}

          <p className={styles.termsText}>
            By using our service you accept our{' '}
            <a
              href="https://tiiny.host/termsofservice.html"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.termsLink}
            >
              terms & conditions
            </a>{' '}
            &{' '}
            <a
              href="https://tiiny.host/privacypolicy.html"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.termsLink}
            >
              privacy policy
            </a>
          </p>
        </div>
      </div>
    </CustomModal>
  )
}

export default FreeTrialModal
